import { getConfig } from "@/features/config/api/get-config";
import { getEnv } from "@/lib/env";
import { Pool, PoolSchema } from "@/models/pool";
import { createFileRoute, useParams } from "@tanstack/react-router";
import TradeMobileButtons from "./-components/TradeMobileButtons";
import TradeSidebar from "./-components/TradeSidebar";
import TradesResizableSection from "./-components/TradesResizableSection";
import Providers from "./-providers";
import { PoolIdProvider } from "./-providers/pool-id-provider";
import { isServer } from "@tanstack/react-query";
import { getPoolQueryKey } from "@/features/pools/api/get-pool-api";

export const Route = createFileRoute("/_main/trade/$chain/$address")({
  component: TradePage,
  loader: async ({ params, context }) => {
    const { address } = params;

    if (!isServer) {
      context.queryClient.refetchQueries({
        queryKey: getPoolQueryKey(address),
      });
      return { pool: null, config: null };
    }

    const API_BASE_URL =
      process.env.VITE_API_URL || "http://localhost:3005/api/v1";

    try {
      const [res, config] = await Promise.all([
        fetch(`${API_BASE_URL}/pools/private/${address}`, {
          headers: { "Api-Key": getEnv.BACKEND_API_KEY! },
          cache: "no-store",
          next: { revalidate: 0 },
        }),
        getConfig(),
      ]);

      if (!res.ok) {
        throw new Error(`Failed to fetch pool: ${res.status}`);
      }
      const json = (await res.json()) as Pool;
      const pool = PoolSchema.parse(json);
      return { pool, config };
    } catch {
      return { pool: null, config: null };
    }
  },

  head: async ({ loaderData }) => {
    const { pool, config } = loaderData!;
    const siteUrl = (getEnv.VITE_SITE_URL ?? "https://terminal.reactor.trade").replace(
      /\/$/,
      "",
    );

    if (!pool || !config) {
      const title = "Reactor | Trade";
      const description = "Trade everything - memes, stocks, perps, defAI";
      const fallbackImage = `${siteUrl}/images/og-card.png`;
      const pageUrl = `${siteUrl}/trade`;
      return {
        meta: [
          { title },
          { name: "description", content: description },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
          { property: "og:type", content: "website" },
          { property: "og:site_name", content: "Reactor" },
          { property: "og:locale", content: "en_US" },
          { property: "og:url", content: pageUrl },
          { property: "og:image", content: fallbackImage },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: title },
          { name: "twitter:description", content: description },
          { name: "twitter:image", content: fallbackImage },
        ],
      };
    }
    const token = pool.tokens.base;
    const dex = config?.dexes.find((d) => d.protocols.includes(pool.protocol));
    const dexName = dex?.name ?? "DEX";
    const title = `${token.symbol} | Trade on ${dexName} | Reactor`;
    const description = `Live ${token.name} (${token.symbol}) trading on ${dexName}. Charts, orders, and stats.`;
    const encodedChain = encodeURIComponent(pool.chain);
    const encodedAddress = encodeURIComponent(pool.id);
    const imageUrl = `${siteUrl}/og/trade/${encodedChain}/${encodedAddress}/png`;
    const pageUrl = `${siteUrl}/trade/${pool.chain}/${pool.id}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: imageUrl },
        { property: "og:image:width", content: "734" },
        { property: "og:image:height", content: "425" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Reactor" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: pageUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageUrl },
      ],
    };
  },
});

function TradePage() {
  const { chain, address } = useParams({
    from: "/_main/trade/$chain/$address",
  });

  console.log("Params", address);
  // const { data: pool } = usePool();

  // if (!chain || !address) {
  //   return <Navigate to="/pump" replace />;
  // }

  // if (!pool) return <Navigate to="/pump" replace />;
  // if (pool?.migrated_to_id) {
  //   return (
  //     <Navigate
  //       to="/trade/$chain/$address"
  //       params={{ chain, address: pool.migrated_to_id }}
  //       replace
  //     />
  //   );
  // }

  return (
    <PoolIdProvider poolId={address} chain={chain}>
      <Providers>
        <TradesResizableSection poolId={address} isLoading={false} />
        <div className="md:hidden">
          <TradeMobileButtons />
        </div>
        <div className="hidden md:block">
          <TradeSidebar />
        </div>
      </Providers>
    </PoolIdProvider>
  );
}
